/**
 * Room array that holds the EnergiSync devices this page lists. Matches the
 * key used everywhere else on the house document (see `config.deviceType`).
 */
export const ENERGISYNC_KEY = 'energiSync';

/**
 * The two sync columns.
 *
 * The device stores each schedule as flat numbers — `on_hour` / `on_minute`,
 * `start_day` / `start_month` / `start_year` and so on. A field here is the
 * single control the user sees, and it names the raw keys it is built from, so
 * one `time` input covers an hour/minute pair and one `date` input covers a
 * day/month/year triplet.
 */
export const SCHEDULE_SECTIONS = [
  {
    key: 'defaultSchedule',
    // The device reports what it is running on `current*`; the cloud asks for a
    // change by writing `expected*`. They differ while a change is in flight.
    readKey: 'currentDefaultSchedule',
    writeKey: 'expectedDefaultSchedule',
    label: 'Default Schedule Sync',
    short: 'Default',
    hint: 'The schedule the device runs every day until a temp schedule overrides it.',
    accent: '#2050ff',
    accentSoft: 'rgba(32,80,255,0.09)',
    fields: [
      { key: 'active', label: 'Active', type: 'boolean', rawKey: 'active' },
      { key: 'on', label: 'ON Time', type: 'time', hourKey: 'on_hour', minuteKey: 'on_minute' },
      { key: 'off', label: 'OFF Time', type: 'time', hourKey: 'off_hour', minuteKey: 'off_minute' },
      { key: 'timestamp', label: 'Last Updated', type: 'timestamp', rawKey: 'timestamp', readOnly: true },
    ],
  },
  {
    key: 'tempSchedule',
    readKey: 'currentTempSchedule',
    writeKey: 'expectedTempSchedule',
    label: 'Temp Schedule Sync',
    short: 'Temp',
    hint: 'A dated override that takes over from the default schedule while it is running.',
    accent: '#f59e0b',
    accentSoft: 'rgba(245,158,11,0.12)',
    fields: [
      { key: 'active', label: 'Active', type: 'boolean', rawKey: 'active' },
      { key: 'on', label: 'ON Time', type: 'time', hourKey: 'on_hour', minuteKey: 'on_minute' },
      { key: 'off', label: 'OFF Time', type: 'time', hourKey: 'off_hour', minuteKey: 'off_minute' },
      // A temp schedule is only ever booked for the week ahead, so both dates are
      // bounded to today .. today + 7 days — in the picker and on save.
      {
        key: 'start',
        label: 'Starts On',
        type: 'date',
        dayKey: 'start_day',
        monthKey: 'start_month',
        yearKey: 'start_year',
        minOffsetDays: 0,
        maxOffsetDays: 7,
      },
      {
        key: 'end',
        label: 'Ends On',
        type: 'date',
        dayKey: 'end_day',
        monthKey: 'end_month',
        yearKey: 'end_year',
        minOffsetDays: 0,
        maxOffsetDays: 7,
        // The window cannot run backwards, so the start date floors this one —
        // inclusively, since a schedule may start and end on the same day.
        after: { field: 'start', inclusive: true, message: 'Cannot be before the start date' },
      },
      { key: 'timestamp', label: 'Last Updated', type: 'timestamp', rawKey: 'timestamp', readOnly: true },
    ],
  },
];

export const theme = {
  accent: '#2050ff',
  accentSoft: 'rgba(32,80,255,0.08)',
  border: '#e5e7eb',
  text: '#111827',
  textMuted: '#6b7280',
  textFaint: '#9ca3af',
  surface: '#fff',
  surfaceMuted: '#f9fafb',
  danger: '#ef4444',
  success: '#10b981',
  font: 'Manrope',
};
